import { createContext, useContext } from "react";

/**
 * Assets are addressed by storage key, never by filesystem path. The editor
 * resolves keys through the API, the renderer through a short-lived local file
 * server — the composition itself stays unaware of either, which is what lets
 * the same component tree serve both.
 */
export const AssetBaseContext = createContext<string>("/assets");

/**
 * Keys already resolved to absolute URLs, for callers that cannot use a base.
 *
 * Client-side rendering is one. Its media reader fetches with `Range` headers,
 * which are not CORS-safelisted, so the request is preflighted — and a
 * preflighted request may not follow a cross-origin redirect. `/assets/<key>`
 * on object storage answers with exactly that: a 302 to a presigned bucket URL.
 * The browser refuses it, and reports "Network error while fetching the video
 * (possibly CORS)", which sends you looking at the bucket's CORS rules. They
 * are fine. The redirect is the problem.
 *
 * A `<video>` element does not hit this, because media elements handle
 * redirects and ranges outside the fetch and CORS model — which is why the
 * editor preview plays the same file happily.
 *
 * So the caller resolves every key up front and passes the answers in. No
 * redirect, the request is cross-origin from the start, and it preflights
 * against the bucket, which allows it.
 */
export const AssetUrlsContext = createContext<Readonly<Record<string, string>> | null>(null);

export function useAssetUrl(): (key: string | undefined) => string | undefined {
  const base = useContext(AssetBaseContext);
  const resolved = useContext(AssetUrlsContext);

  return (key) => {
    if (!key) return undefined;
    // A resolved URL wins, and a missing one falls through rather than
    // returning nothing: a key the map does not know about is better served by
    // the base than not served at all.
    const direct = resolved?.[key];
    if (direct) return direct;

    const clean = base.replace(/\/+$/, "");
    return `${clean}/${key.split("/").map(encodeURIComponent).join("/")}`;
  };
}
